import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Catches a console page that failed to render — most often its lazy chunk not
 * downloading (a flaky school network, or a deploy that replaced the chunk a
 * stale tab asks for). Without it the whole app would go blank; with it the
 * sidebar stays and the page says what happened and offers a reload, which
 * fetches the current build.
 *
 * Keyed by route in AppRouter, so moving to another page clears the error.
 */
export class ConsoleScreenBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Console page failed to render', error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <div role="alert" className="console font-dash rounded-[14px] border border-line bg-surface px-6 py-10 text-center">
        <p className="text-[15px] font-semibold text-ink">This page didn’t load</p>
        <p className="mx-auto mt-1 max-w-sm text-[13px] leading-5 text-muted">
          Check the connection and reload. If Kalqy was just updated, reloading picks up the new version.
        </p>
        <button
          type="button"
          onClick={() => {
            window.location.reload()
          }}
          className="mt-4 inline-flex h-8 items-center rounded-[8px] bg-primary px-3 text-[13px] font-medium text-primary-ink hover:opacity-90"
        >
          Reload
        </button>
      </div>
    )
  }
}
